import * as THREE from 'three';
import { fbm2 } from '../../core/rng';
import { createAurora } from '../aurora';
import type { BuiltLocation, LocationDef } from '../Location';
import { paintableSnowMaterial, registerPaintable } from '../paintable';
import { disposeScene, pine, skyDome, stars, stdMat, sun } from '../props';
import { createTerrain } from '../terrain';

/**
 * Frozen lake under the aurora borealis — a wide expanse of snow-covered ice
 * at night. The showcase level: effectively an infinite canvas, with the
 * aurora subtly tinting the snow.
 */
export const auroraLakeLocation: LocationDef = {
  id: 'aurora-lake',
  name: 'Aurora Lake',
  description: 'A frozen lake at night beneath dancing northern lights. The endless canvas.',
  thumbnail: { skyTop: '#05070f', skyBottom: '#16283e', ground: '#c9d6e8', accent: '#3ef29a' },

  build(ctx): BuiltLocation {
    const scene = new THREE.Scene();
    const colliders: THREE.Box3[] = [];
    const paintables: BuiltLocation['paintables'] = [];

    scene.fog = new THREE.FogExp2(0x0a1524, 0.0035);
    scene.add(
      skyDome([
        [0, '#04060e'],
        [0.5, '#0a1526'],
        [0.78, '#16283e'],
        [1, '#1d3450'],
      ]),
    );
    scene.add(stars(900));

    const aurora = createAurora();
    scene.add(aurora.group);

    const hemi = new THREE.HemisphereLight(0x33507a, 0x101822, 0.85);
    scene.add(hemi);
    const moon = sun(0xa8c0e8, 0.55, -60, 90, -40, 90);
    scene.add(moon);

    const height = (x: number, z: number) => fbm2(x * 0.02, z * 0.02, ctx.seed) * 0.5;
    const snow = paintableSnowMaterial({ color: 0xe9eef8, grainScale: 2400 });
    const terrain = createTerrain({
      size: 320,
      segments: 110,
      height,
      material: snow.material,
    });
    scene.add(terrain.mesh);
    registerPaintable(paintables, 'ground', terrain.mesh, snow.uniform, true);

    // Distant tree line so the horizon isn't empty.
    let s = ctx.seed + 7;
    const rand = () => {
      s = (s * 16807) % 2147483647;
      return s / 2147483647;
    };
    for (let i = 0; i < 26; i++) {
      const angle = (i / 26) * Math.PI * 2 + rand() * 0.2;
      const r = 105 + rand() * 35;
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      scene.add(pine(x, z, height(x, z), 1.2 + rand() * 1.4, colliders));
    }

    // A little ice-fishing hut.
    const hut = new THREE.Group();
    const hutWood = stdMat(0x6b4a35, 0.9);
    const hutBody = new THREE.Mesh(new THREE.BoxGeometry(2.4, 2.2, 2.4), hutWood);
    hutBody.position.y = 1.1;
    hutBody.castShadow = true;
    hut.add(hutBody);
    const hutRoof = new THREE.Mesh(new THREE.ConeGeometry(2.1, 1.1, 4), stdMat(0xe9eef6, 0.95));
    hutRoof.position.y = 2.75;
    hutRoof.rotation.y = Math.PI / 4;
    hut.add(hutRoof);
    const lantern = new THREE.PointLight(0xffc37a, 26, 14, 2);
    lantern.position.set(1.6, 1.4, 0);
    hut.add(lantern);
    hut.position.set(30, height(30, -25), -25);
    scene.add(hut);
    colliders.push(
      new THREE.Box3(new THREE.Vector3(28.7, 0, -26.3), new THREE.Vector3(31.3, 3, -23.7)),
    );

    return {
      scene,
      spawn: { pos: new THREE.Vector3(0, height(0, 6), 6), yaw: 0 },
      paintables,
      colliders,
      groundHeight: height,
      groundSurfaceIdAt: () => 'ground',
      bounds: { minX: -150, maxX: 150, minZ: -150, maxZ: 150 },
      baseFogDensity: 0.0035,
      update(_dt, elapsed) {
        aurora.update(elapsed);
        // The aurora subtly tints the snow via the hemisphere light.
        const glow = aurora.glow(elapsed);
        hemi.color.setRGB(0.2 + glow * 0.05, 0.31 + glow * 0.17, 0.48 - glow * 0.02);
        hemi.intensity = 0.75 + glow * 0.35;
      },
      dispose() {
        disposeScene(scene);
      },
    };
  },
};
